import React, { useEffect, useMemo, useState } from 'react';
import { subscriptionAPI, billingAPI, authUtils } from '../api/userAPI';
import { useAuth } from '../context/AuthContext';
import '../components/auth.css';

const PricingPage = () => {
  const { isAuthenticated, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submittingTier, setSubmittingTier] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  useEffect(() => {
      loadSubscriptionData();
      // Check if we're returning from Stripe checkout
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const userId = urlParams.get('user_id');
      
      if (sessionId && userId) {
        handleCheckoutSuccess(sessionId);
      }
    }, []);
  
  const handleCheckoutSuccess = async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
        
      // Sync the checkout session
      const result = await billingAPI.syncSession(sessionId);
        
      if (result.success) {
        setSuccessMessage(`Successfully upgraded to ${result.tier} plan!`);
        setShowSuccess(true);
          
        // Refresh user data
        await refreshUser();
        await loadSubscriptionData();
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
          
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        throw new Error(result.message || 'Failed to sync session');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const loadSubscriptionData = async () => {
     try {
       setLoading(true);
       setError(null);
 
       const [plansData, currentData, usageData] = await Promise.all([
         subscriptionAPI.getPlans(),
         subscriptionAPI.getCurrent(),
         subscriptionAPI.getUsage()
       ]);

       const rawPlans = plansData.plans || {};
       const normalizedPlans = Object.fromEntries(
          Object.entries(rawPlans).map(([key, value]) => [String(key).toUpperCase(), value])
        );
 
       setPlans(normalizedPlans);
       const activeTier = currentData.tier || currentData.subscription_tier || null;
       setCurrentSubscription(activeTier);
       setUsage(usageData);
     } catch (err) {
       setError(err.message);
     } finally {
       setLoading(false);
     }
   };

  // useEffect(() => {
  //   let mounted = true;
  //   const loadPlans = async () => {
  //     try {
  //       setLoading(true);
  //       setError(null);
  //       const response = await subscriptionAPI.getPlans();
  //       console.log('PricingPage - plans response:', response);
  //       // Response shape from backend: { plans: { free|basic|...: {...} }, currency, billing_cycles }
  //       const rawPlans = response?.plans || {};
  //       // Normalize keys to uppercase for consistent lookup
  //       const normalized = Object.fromEntries(
  //         Object.entries(rawPlans).map(([key, value]) => [String(key).toUpperCase(), value])
  //       );
  //       if (mounted) setPlans(normalized);
  //     } catch (e) {
  //       if (mounted) setError(e.message || 'Failed to load plans');
  //     } finally {
  //       if (mounted) setLoading(false);
  //     }
  //   };
  //   loadPlans();
  //   return () => {
  //     mounted = false;
  //   };
  // }, []);

  const handleSelectPlan = async (tierKey) => {
    if (!isAuthenticated) {
      setError('Please sign in or create an account to select a plan.');
      return;
    }

    try {
      setSubmittingTier(tierKey);
      setError(null);
      
      // Create Stripe checkout session
      const checkoutResult = await billingAPI.createCheckout(tierKey.toLowerCase(), 'monthly');
      
      if (checkoutResult.success && checkoutResult.url) {
        // Redirect to Stripe checkout
        window.location.href = checkoutResult.url;
      } else {
        throw new Error(checkoutResult.message || 'Failed to create checkout session');
      }
    } catch (e) {
      setError(e.message || 'Failed to create checkout session');
      setSubmittingTier(null);
    }
  };

  if (loading) {
    return (
      <div>
        <h2>Plans</h2>
        <p className="loading">Loading plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2>Plans</h2>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  const tiersInOrder = ['FREE', 'BASIC', 'PROFESSIONAL', 'ENTERPRISE'];

  return (
    <div className="subscription-plans">
      <h2>Choose your plan</h2>
      <p style={{ color: 'var(--text-muted, #666)', marginBottom: '16px' }}>Select a subscription tier. You can upgrade or downgrade later.</p>

      {showSuccess && (
        <div className="message success" style={{ marginTop: '12px', marginBottom: '16px' }}>
          {successMessage}
        </div>
      )}

      <div className="plans-grid">
        {tiersInOrder.map((key) => {
          const plan = plans?.[key];
          if (!plan) return null;
          const isCurrent = (currentSubscription || '').toString().toLowerCase() === key.toLowerCase();
          return (
            <div key={key} className={`plan-card ${isCurrent ? 'current' : ''}`}>
              <div className="plan-header">
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  <span className="price">${plan.price_monthly}/mo</span>
                  <span className="yearly-price">or ${plan.price_yearly}/yr</span>
                </div>
              </div>
              <div className="plan-features">
                <ul>
                  {(plan.features || []).map((f, idx) => (
                    <li key={idx}>{f}</li>
                  ))}
                </ul>
              </div>
              <div className="plan-limits">
                <div className="limit-item">
                  <span>Rate limit:</span>
                  <span>{plan.rate_limit_per_minute}/min</span>
                </div>
                <div className="limit-item">
                  <span>Concurrent:</span>
                  <span>{plan.concurrent_requests}</span>
                </div>
                <div className="limit-item">
                  <span>Monthly API calls:</span>
                  <span>{plan.max_api_calls_per_month}</span>
                </div>
              </div>
              <div className="plan-actions">
                <button
                  className={`btn btn-primary ${isCurrent ? 'current' : ''}`}
                  onClick={() => handleSelectPlan(key)}
                  disabled={isCurrent || submittingTier === key}
                  style={{ width: '100%' }}
                >
                  {isCurrent ? 'Current Plan' : (submittingTier === key ? 'Updating...' : 'Select')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingPage;


